import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import DataTable from '../components/DataTable';
import { tipoCambioService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const TipoCambio = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [form, setForm] = useState({ fecha: '', tc_compra: '', tc_venta: '' });

  // SUNAT
  const [showSunatModal, setShowSunatModal] = useState(false);
  const [sunatAnio, setSunatAnio] = useState(new Date().getFullYear());
  const [sunatMes, setSunatMes] = useState(new Date().getMonth() + 1);
  const [sunatPreview, setSunatPreview] = useState([]);
  const [sunatLoading, setSunatLoading] = useState(false);
  const [importando, setImportando] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tipoCambioService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar tipos de cambio.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const fecha = row['Fecha'] || row['fecha'] || '';
        const tc_compra = row['T.C. Compra'] || row['tc_compra'] || 0;
        const tc_venta = row['T.C. Venta'] || row['tc_venta'] || 0;
        let errorMsg = null;
        if (!fecha) {
          fail++; errorMsg = `Fila ${i + 1}: Fecha vacia (obligatoria)`;
        } else {
          try {
            await tipoCambioService.create({ fecha: String(fecha), tc_compra: parseFloat(tc_compra), tc_venta: parseFloat(tc_venta) });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} (${fecha}): ${getErrorDetail(err, 'Error al crear tipo de cambio')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setForm({ fecha: today, tc_compra: '', tc_venta: '' });
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (r) => {
    const fecha = r.fecha ? r.fecha.split('T')[0] : r.fecha;
    setEditing(r);
    setForm({ fecha, tc_compra: r.tc_compra.toString(), tc_venta: r.tc_venta.toString() });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fecha || !form.tc_compra || !form.tc_venta) { setAlert({ type: 'warning', message: 'Todos los campos son obligatorios.' }); return; }
    const data = { fecha: form.fecha, tc_compra: parseFloat(form.tc_compra), tc_venta: parseFloat(form.tc_venta) };
    try {
      if (editing) {
        await tipoCambioService.update(editing.id_tipo_cambio, data);
      } else {
        await tipoCambioService.create(data);
      }
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar.' });
    }
  };

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('es-PE');
  };

  // ===== SUNAT =====
  const openSunat = () => {
    setSunatPreview([]);
    setShowSunatModal(true);
  };

  const consultarSunat = async () => {
    setSunatLoading(true);
    setSunatPreview([]);
    try {
      const res = await tipoCambioService.consultarSunat(sunatAnio, sunatMes);
      setSunatPreview(res.data);
      if (res.data.length === 0) {
        setAlert({ type: 'warning', message: 'No se encontraron datos en SUNAT para ese periodo.' });
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al consultar SUNAT.' });
    } finally {
      setSunatLoading(false);
    }
  };

  const importarSunat = async () => {
    setImportando(true);
    try {
      const res = await tipoCambioService.importarSunat(sunatAnio, sunatMes);
      const { creados, omitidos } = res.data;
      setAlert({ type: 'success', message: `Importados: ${creados} registros. Omitidos (duplicados): ${omitidos}.` });
      setShowSunatModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al importar desde SUNAT.' });
    } finally {
      setImportando(false);
    }
  };

  const columns = [
    { key: 'fecha', label: 'Fecha', width: 120, style: { fontWeight: 600 }, render: (r) => formatDate(r.fecha) },
    { key: 'tc_compra', label: 'T.C. Compra', width: 130, render: (r) => parseFloat(r.tc_compra).toFixed(4) },
    { key: 'tc_venta', label: 'T.C. Venta', width: 130, render: (r) => parseFloat(r.tc_venta).toFixed(4) },
  ];

  const importCols = [
    { key: 'Fecha', label: 'Fecha', required: true, example: '2026-01-15', description: 'Fecha en formato AAAA-MM-DD' },
    { key: 'T.C. Compra', label: 'T.C. Compra', required: false, example: '3.7500', description: 'Tipo de cambio compra (hasta 4 decimales)' },
    { key: 'T.C. Venta', label: 'T.C. Venta', required: false, example: '3.8000', description: 'Tipo de cambio venta (hasta 4 decimales)' },
  ];

  const aniosOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) aniosOptions.push(y);

  return (
    <Layout title="Tipo de Cambio" subtitle="Tablas Generales">
      <div className="page-header">
        <h2>Tipo de Cambio</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={openSunat}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/></svg>
            Consultar SUNAT
          </button>
          <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={records}
        idField="id_tipo_cambio"
        onEdit={openEdit}
        loading={loading}
        emptyMessage="No hay registros"
        exportFileName="tipo_cambio"
        importColumns={importCols}
      />

      {/* Modal crear/editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{editing ? 'Editar T.C.' : 'Nuevo Tipo de Cambio'}</h2><button className="modal-close" onClick={() => setShowModal(false)}>&times;</button></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row"><label>Fecha *</label><input type="date" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})} /></div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                  <div className="form-row"><label>T.C. Compra *</label><input type="number" step="0.0001" value={form.tc_compra} onChange={e => setForm({...form, tc_compra: e.target.value})} placeholder="3.7500" /></div>
                  <div className="form-row"><label>T.C. Venta *</label><input type="number" step="0.0001" value={form.tc_venta} onChange={e => setForm({...form, tc_venta: e.target.value})} placeholder="3.8000" /></div>
                </div>
              </div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button><button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Registrar'}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Modal SUNAT */}
      {showSunatModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Consultar T.C. SUNAT</h2>
              <button className="modal-close" onClick={() => setShowSunatModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: 16 }}>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Anio</label>
                  <select value={sunatAnio} onChange={e => setSunatAnio(parseInt(e.target.value))}>
                    {aniosOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Mes</label>
                  <select value={sunatMes} onChange={e => setSunatMes(parseInt(e.target.value))}>
                    {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-primary" onClick={consultarSunat} disabled={sunatLoading} style={{ whiteSpace: 'nowrap' }}>
                  {sunatLoading ? 'Consultando...' : 'Consultar'}
                </button>
              </div>

              {sunatPreview.length > 0 && (
                <>
                  <div style={{ fontSize: 12.5, color: 'rgba(148,163,184,0.7)', marginBottom: 8 }}>
                    {sunatPreview.length} registros encontrados para {MESES[sunatMes - 1]} {sunatAnio}
                  </div>
                  <div className="data-table-container" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Compra</th>
                          <th>Venta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sunatPreview.map((item, i) => (
                          <tr key={i}>
                            <td>{item.fecha}</td>
                            <td>{item.compra != null ? item.compra.toFixed(4) : '--'}</td>
                            <td>{item.venta != null ? item.venta.toFixed(4) : '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowSunatModal(false)}>Cerrar</button>
              {sunatPreview.length > 0 && (
                <button type="button" className="btn btn-primary" onClick={importarSunat} disabled={importando}>
                  {importando ? 'Importando...' : `Importar ${sunatPreview.length} registros`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
    </Layout>
  );
};

export default TipoCambio;
